import { ConfigProvider, Modal, Tag, Tooltip } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import { THEME } from '../../main';

/**
 * Inspection Sample Labeling (PTB-6619).
 *
 * A customer requires every inspected sample to be bagged and labeled with its
 * sample number and the operator's initials. Three screens ask about it -- hand
 * check, Keyence check and delivery -- so the wording lives here rather than
 * being retyped in each one.
 *
 * Both confirmations resolve `false` on cancel, on Esc and on a mask click: a
 * dismissed dialog is an unanswered question, and the caller must treat it the
 * same as an explicit No.
 */

const TOOLTIP =
	'Every inspected sample must be bagged and labeled with its sample number and the operator’s initials.';

/**
 * PTB-6441: a static `Modal.confirm()` mounts outside the ConfigProvider /
 * StyleProvider tree, so Tailwind's preflight strips the primary button's
 * styles and the Yes button renders invisible until it is hovered. Both
 * confirmations here are blocking gates, so an unreadable Yes leaves the
 * operator with only the No they can see. Re-injecting the theme around the
 * footer buttons is the fix already used elsewhere in the app.
 */
const themedFooter = (
	_originNode: React.ReactNode,
	{ OkBtn, CancelBtn }: { OkBtn: React.FC; CancelBtn: React.FC }
) => (
	<ConfigProvider theme={THEME}>
		<CancelBtn />
		<OkBtn />
	</ConfigProvider>
);

export function InspectionSampleLabelingTag({
	style,
}: {
	style?: React.CSSProperties;
}) {
	return (
		<Tooltip title={TOOLTIP}>
			<Tag icon={<TagOutlined />} color="magenta" style={style}>
				Inspection Sample Labeling
			</Tag>
		</Tooltip>
	);
}

/** Asked once per sample, when an operator completes a hand or Keyence check. */
export function confirmSampleLabeling(
	sampleNumber?: number | null
): Promise<boolean> {
	const sample =
		sampleNumber != null ? `sample number ${sampleNumber}` : 'its sample number';

	return new Promise((resolve) => {
		Modal.confirm({
			centered: true,
			width: 520,
			title: 'This job requires Inspection Sample Labeling',
			content: (
				<p style={{ fontSize: 16, marginTop: 12 }}>
					Is the sample in a sample bag and labeled with <b>{sample}</b> along
					with your initials?
				</p>
			),
			okText: 'Yes',
			cancelText: 'No / Cancel',
			footer: themedFooter,
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}

/**
 * Asked once per delivery, before the delivery tag prints.
 *
 * `sampleNumbers` is the samples this delivery is accountable for: inspected,
 * minus whatever an earlier partial delivery already covered. The server
 * recomputes the same set when it records the delivery, so what the operator
 * reads here can go stale if someone else delivers the line item meanwhile.
 */
export function confirmDeliverySampleLabeling(
	sampleNumbers: number[]
): Promise<boolean> {
	const count = sampleNumbers.length;

	return new Promise((resolve) => {
		Modal.confirm({
			centered: true,
			width: 560,
			title: 'This job requires Inspection Sample Labeling',
			content: (
				<div style={{ fontSize: 16, marginTop: 12 }}>
					{count > 0 ? (
						<p>
							Are the {count} inspected {count === 1 ? 'sample' : 'samples'} (
							<b>
								{count === 1 ? 'sample ' : 'samples '}
								{sampleNumbers.join(', ')}
							</b>
							) each in a sample bag, labeled with the sample number and your
							initials?
						</p>
					) : (
						<p>
							Is every inspected sample in a sample bag, labeled with the sample
							number and your initials?
						</p>
					)}
					<p style={{ marginBottom: 0 }}>
						The delivery tag will not print until you confirm.
					</p>
				</div>
			),
			okText: 'Yes, samples are labeled',
			cancelText: 'No / Cancel',
			footer: themedFooter,
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}
